import { Injectable } from '@angular/core';
import { EvaluationFlowsEnum } from '../models/shared.model';
import { ANALYSIS_TIMEOUTS, AnalysisError, AsyncContext } from '../utils/analysis-async';

export type FlowState = 'pending' | 'running' | 'complete' | 'incomplete' | 'skipped';
export interface AnalysisFailure {
  operation: string;
  code: string;
  message: string;
}
export interface FlowStatus {
  state: FlowState;
  failures: AnalysisFailure[];
}

export class AnalysisRun implements AsyncContext {
  readonly controller = new AbortController();
  readonly signal = this.controller.signal;
  readonly deadline = Date.now() + ANALYSIS_TIMEOUTS.run;
  readonly flows = new Map<EvaluationFlowsEnum, FlowStatus>();
  closed = false;
  finished = false;
  headersAvailable = false;
  bodyAvailable = false;
  body: string | null = null;

  constructor(readonly id: number, readonly item: Office.MessageRead,
              private readonly ownsRun: () => boolean) {
    Object.values(EvaluationFlowsEnum).forEach(flow => this.flows.set(flow, { state: 'pending', failures: [] }));
  }

  get active(): boolean { return !this.closed && !this.signal.aborted && this.ownsRun(); }
  assertActive(): void {
    if (!this.active) throw new AnalysisError('cancelled', 'Analysis superseded or finished');
    if (Date.now() >= this.deadline) throw new AnalysisError('timeout', 'Analysis timed out');
  }
  configure(enabled: string[]): void {
    this.assertActive();
    this.flows.forEach((status, flow) => status.state = enabled.includes(flow) ? 'pending' : 'skipped');
  }
  issue(flow: EvaluationFlowsEnum, error: unknown, operation: string): void {
    if (!this.active) return;
    const status = this.flows.get(flow);
    if (!status || status.state === 'skipped') return;
    const failure = {
      operation, code: error instanceof AnalysisError ? error.code : 'failed',
      message: error instanceof Error ? error.message : String(error)
    };
    status.failures = [...status.failures.filter(value => value.operation !== operation), failure];
    if (status.state === 'complete') status.state = 'incomplete';
  }
  resolveIssue(flow: EvaluationFlowsEnum, operation: string): void {
    if (!this.active) return;
    const status = this.flows.get(flow);
    status.failures = status.failures.filter(value => value.operation !== operation);
    if (status.state === 'incomplete' && status.failures.length === 0) status.state = 'complete';
  }
  complete(flow: EvaluationFlowsEnum): void {
    if (!this.active) return;
    const status = this.flows.get(flow);
    if (status.state !== 'skipped') status.state = status.failures.length ? 'incomplete' : 'complete';
  }
  get incomplete(): boolean {
    return [...this.flows.values()].some(status => status.state === 'incomplete' || status.failures.length > 0);
  }
  get verdictAvailable(): boolean {
    return [...this.flows.values()].every(status => status.state === 'complete' || status.state === 'skipped');
  }
  finish(): void { this.finished = true; this.close(); }
  close(): void {
    if (this.closed) return;
    this.closed = true;
    this.controller.abort();
  }
}

@Injectable({ providedIn: 'root' })
export class AnalysisRunService {
  current: AnalysisRun | null = null;
  private sequence = 0;
  begin(item: Office.MessageRead): AnalysisRun {
    this.current?.close();
    const run = new AnalysisRun(++this.sequence, item, () => this.current === run);
    this.current = run;
    return run;
  }
  cancel(): void { this.current?.close(); }
}
